import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type SlotStorage = {
  Dock: string;
  id?: string;
  Storage: string;
};

export type GlobalStorage = {
  docks: string[];
  storage: string;
};

export type SlotStorageContacts = {
  code?: string;
  contactType?: string;
  email?: string;
  firstName?: string;
  id?: string;
  identityKey?: string;
  lastName?: string;
  validFrom?: string;
  validTo?: string;
};

export type SlotStorageResult = {
  createdBy: string;
  dateCreated: string;
  dateModified: string;
  Dock: string;
  id: string;
  modifiedBy: string;
  storage: string;
};

export type SlotsStorageDetail = {
  dock: string;
  id: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  storage: string;
};

type BaseWorkingHour = {
  day: string;
  from: string;
  to: string;
  validFrom: string;
  validTo: string;
};

export type WorkingHourPayload = BaseWorkingHour & {
  dock: string;
  storage: string;
  workingHourId?: string;
};

export type WorkingHourResponse = BaseWorkingHour & {
  workingHourId: string;
};

export enum EnumSlotsStorageViews {
  EDIT = 0,
  CONTACTS = 1,
  WORKING_HOURS = 2,
}

export enum EnumSlotsStorageFieldStructure {
  SlotsStorageTable = 0,
  SlotsStorageCreateForm = 1,
  SlotsStorageEditForm = 2,
  NestedContactsTable = 3,
  NestedContactsForm = 4,
  NestedWorkingHoursTable = 6,
  NestedWorkingHoursForm = 7,
}

export type ErrorDetail = {
  detail?: string;
  errors?: Record<string, string[]>;
  message?: string;
};

export type ErrorWrapper = {
  detail?: string;
  error?: ErrorDetail & { errors?: Record<string, string[]> };
  errors?: Record<string, string[]>;
  message?: string;
  payload?: ErrorDetail;
};

export interface SlotsStorageFields {
  /**
   * Required for form
   */
  controlType?: SlotsStoragePlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: SlotsStoragePlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: SlotsStoragePlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: SlotsStoragePlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: SlotsStoragePlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: SlotsStoragePlaceHolderTypeVal;
  /**
   * Shows if it should be visible by default in forms
   */
  visible?: SlotsStoragePlaceHolderTypeVal;
}

export interface SlotsStoragePlaceHolderTypeVal {
  createForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
  nestedContactsForm?: boolean | number | string;
  nestedWorkingHoursForm?: boolean | number | string;
}

export class SlotsStorageField {
  fields: MappedField[];
  userRightFields: string[] = [];
  hasField: (
    field: SlotsStorageFields,
    refId?: keyof SlotsStoragePlaceHolderTypeVal,
    actualId?: keyof SlotsStoragePlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumSlotsStorageFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  getDefaultFields(type?: EnumSlotsStorageFieldStructure): MappedField[] {
    switch (type) {
      case EnumSlotsStorageFieldStructure.SlotsStorageTable:
        return this.getDatagridFields();
      case EnumSlotsStorageFieldStructure.SlotsStorageCreateForm:
        return this.getCreateFormList();
      case EnumSlotsStorageFieldStructure.SlotsStorageEditForm:
        return this.getEditFormList();
      case EnumSlotsStorageFieldStructure.NestedContactsTable:
        return this.getContactsFields();
      case EnumSlotsStorageFieldStructure.NestedContactsForm:
        return this.getContactsFormFields();
      case EnumSlotsStorageFieldStructure.NestedWorkingHoursTable:
        return this.getWorkingHoursFields();
      case EnumSlotsStorageFieldStructure.NestedWorkingHoursForm:
        return this.getWorkingHoursFormFields();
      default:
        return this.getDatagridFields();
    }
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: SlotsStorageFields,
    refId?: keyof SlotsStoragePlaceHolderTypeVal,
    actualId?: keyof SlotsStoragePlaceHolderTypeVal,
  ): boolean => {
    if (this.userRightFields && !(field.isRequired && actualId && field.isRequired[actualId])) {
      return (
        this.userRightFields.includes(String((refId && field.id[refId]) ?? '').toLowerCase()) &&
        !(field.exclude && actualId && field.exclude[actualId] === true)
      );
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };

  validFunWithoutUserFields = (
    field: SlotsStorageFields,
    refId?: keyof SlotsStoragePlaceHolderTypeVal,
    actualId?: keyof SlotsStoragePlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && actualId && field.exclude[actualId] === true) {
      return false;
    } else {
      return true;
    }
  };

  getDatagridFields(): MappedField[] {
    return this.slotsStorageFields
      .filter((field: SlotsStorageFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotsStorageFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCreateFormList(): MappedField[] {
    return this.slotsStorageFields
      .filter((field: SlotsStorageFields): boolean => {
        return this.hasField(field, 'datagrid', 'createForm');
      })
      .map((field: SlotsStorageFields): MappedField => {
        return {
          controlType: (field.controlType?.createForm as string) ?? '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.createForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getEditFormList(): MappedField[] {
    return this.slotsStorageFields
      .filter((field: SlotsStorageFields): boolean => {
        return this.hasField(field, 'datagrid', 'editForm');
      })
      .map((field: SlotsStorageFields): MappedField => {
        return {
          controlType: (field.controlType?.editForm as string) ?? '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.editForm as string) ?? (field.headerText?.datagrid as string) ?? '',
          isRequired: !!(field.isRequired && field.isRequired.editForm),
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotsStorageFields: SlotsStorageFields[] = [
    {
      dataType: { datagrid: 'actions' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple', editForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Storage' },
      id: { datagrid: 'storage' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple', editForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Dock' },
      id: { datagrid: 'dock' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
  ];

  getContactsFields(): MappedField[] {
    return this.slotsStorageContactsFields
      .filter((field: SlotsStorageFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotsStorageFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getContactsFormFields(): MappedField[] {
    return this.slotsStorageContactsFields
      .filter((field: SlotsStorageFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedContactsForm');
      })
      .map((field: SlotsStorageFields): MappedField => {
        return {
          controlType: (field.controlType?.nestedContactsForm as string) ?? '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotsStorageContactsFields: SlotsStorageFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedContactsForm: 'd-storage' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Storage' },
      id: { datagrid: 'storage' },
      visible: { datagrid: false },
      // exclude: { datagrid: true }
    },
    {
      controlType: { nestedContactsForm: 'd-identityKey' },
      dataType: { datagrid: 'string' },
      exclude: { datagrid: true },
      headerText: { datagrid: 'Identity Key' },
      id: { datagrid: 'identityKey' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedContactsForm: 'dropdown' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Contact Type' },
      id: { datagrid: 'contactType' },
      isRequired: { nestedContactsForm: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'First Name' },
      id: { datagrid: 'firstName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Last Name' },
      id: { datagrid: 'lastName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email' },
      id: { datagrid: 'email' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedContactsForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid From' },
      id: { datagrid: 'validFrom' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedContactsForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid To' },
      id: { datagrid: 'validTo' },
      visible: { datagrid: true },
    },
  ];

  getWorkingHoursFields(): MappedField[] {
    return this.slotsStorageWorkingHoursFields
      .filter((field: SlotsStorageFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: SlotsStorageFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type:
            field.dataType && field.dataType.datagrid
              ? (field.dataType.datagrid as string)
              : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getWorkingHoursFormFields(): MappedField[] {
    return this.slotsStorageWorkingHoursFields
      .filter((field: SlotsStorageFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedWorkingHoursForm');
      })
      .map((field: SlotsStorageFields): MappedField => {
        return {
          controlType: (field.controlType?.nestedWorkingHoursForm as string) ?? '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!(field.isRequired && field.isRequired.nestedWorkingHoursForm),
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly slotsStorageWorkingHoursFields: SlotsStorageFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedWorkingHoursForm: 'dropdown' },
      headerText: { datagrid: 'Day' },
      id: { datagrid: 'day' },
      isRequired: { nestedWorkingHoursForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedWorkingHoursForm: 'dateTime' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'From' },
      id: { datagrid: 'from' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedWorkingHoursForm: 'dateTime' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'To' },
      id: { datagrid: 'to' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedWorkingHoursForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid From' },
      id: { datagrid: 'validFrom' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedWorkingHoursForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid To' },
      id: { datagrid: 'validTo' },
      visible: { datagrid: true },
    },
  ];
}
